import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { ShopService } from '@app/services/shops/shop.service';
import { CurrencySign } from '@pdqdev/pdq-shared/dist/entities';
import {
  DigitalUpsellFormShape,
  DIGITAL_UPSELL_STARTER_TEMPLATE,
  DIGITAL_UPSELL_VARIABLES,
} from './digital-upsell.model';
import { DISCOUNT_LABEL_PLACEHOLDER, DISCOUNT_TYPE_OPTIONS } from '../../discount.model';
import { TimerPlacementOption } from '../../components/timer-config/timer-config.component';
import { TimerPreviewState } from '../../components/timer-config/timer.model';
import { SmartCartPreviewStateService } from '../../preview/smart-cart-preview-state.service';
import { readDisplayConditionsGroup } from '../../components/smart-cart-form-display-conditions/display-conditions.model';

const ALLOWED_PRODUCT_IMAGE_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg'];
const MAX_PRODUCT_IMAGE_BYTES = 100 * 1024;

type DigitalUpsellEditorTab = 'settings' | 'timer' | 'display-conditions';

@Component({
  selector: 'smart-cart-form-digital-upsell-editor',
  templateUrl: './digital-upsell.component.html',
  styleUrls: ['./digital-upsell.component.scss'],
  standalone: false,
})
export class SmartCartFormDigitalUpsellEditorComponent implements OnInit {
  @Input({ required: true }) formGroup!: FormGroup<DigitalUpsellFormShape>;
  @Input() usageToken = '';
  @Input() smartCartId: number | null = null;

  readonly placementOptions: TimerPlacementOption[] = [
    { value: 'inline', text: 'Inline' },
    { value: 'badge', text: 'Badge' },
  ];

  readonly customCodeVariables = DIGITAL_UPSELL_VARIABLES;
  readonly customCodeStarterTemplate = DIGITAL_UPSELL_STARTER_TEMPLATE;

  activeTab: DigitalUpsellEditorTab = 'settings';
  imageValidationError: string | null = null;
  isTooltipEnabled = false;
  isPercent = false;
  isDiscountEnabled = false;
  isDiscountAppliedToUpsellItem = false;
  productImageUrl = '';
  descriptionInitial = '';
  tooltipContentInitial = '';

  rangeError = false;
  rangeMessage = '';

  readonly allowedProductImageTypes = ALLOWED_PRODUCT_IMAGE_TYPES;
  readonly maxProductImageBytes = MAX_PRODUCT_IMAGE_BYTES;
  readonly tabs: { label: string; value: DigitalUpsellEditorTab }[] = [
    { label: 'Settings', value: 'settings' },
    { label: 'Timer', value: 'timer' },
    { label: 'Display Conditions', value: 'display-conditions' },
  ];
  readonly imageFitOptions: DropdownOption[] = [
    { label: 'Contain', value: 'contain' },
    { label: 'Cover', value: 'cover' },
  ];
  readonly discountApplyToOptions: DropdownOption[] = [
    { label: 'Upsell item', value: 'upsellItem' },
    { label: 'Order subtotal', value: 'orderSubtotal' },
  ];
  readonly discountTypeOptions = DISCOUNT_TYPE_OPTIONS;
  readonly discountLabelPlaceholder = DISCOUNT_LABEL_PLACEHOLDER;
  readonly currencySymbol: CurrencySign = inject(ShopService).shopCurrencySymbol ?? '$';

  private readonly destroyRef = inject(DestroyRef);
  private readonly previewState = inject(SmartCartPreviewStateService);

  readonly timerPreviewState = this.previewState.digitalUpsellPreviewTimerState;

  ngOnInit(): void {
    const productImage = this.formGroup.controls.productImageBase64;
    const tooltip = this.formGroup.controls.tooltip.controls;

    this.descriptionInitial = this.formGroup.controls.description.value;
    this.tooltipContentInitial = tooltip.content.value;
    this.isTooltipEnabled = tooltip.enabled.value;
    this.productImageUrl = productImage.value;

    this.isPercent = this.formGroup.controls.pricingMethod.value === 'percent';
    this.updatePercentValidity();

    const discount = this.formGroup.controls.discountConfiguration.controls;
    this.isDiscountEnabled = discount.enabled.value;
    discount.enabled.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => (this.isDiscountEnabled = enabled));

    this.isDiscountAppliedToUpsellItem = discount.applyTo.value === 'upsellItem';
    discount.applyTo.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((applyTo) => (this.isDiscountAppliedToUpsellItem = applyTo === 'upsellItem'));

    tooltip.enabled.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((enabled) => {
      this.isTooltipEnabled = enabled;
      // Re-seed the editor from the live value so re-enabling doesn't reopen a stale snapshot.
      if (enabled) this.tooltipContentInitial = tooltip.content.value;
    });

    productImage.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => (this.productImageUrl = value));

    this.formGroup.controls.pricingMethod.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((method) => {
      this.isPercent = method === 'percent';
      this.updatePercentValidity();
    });

    this.formGroup.controls.percent.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updatePercentValidity());
  }

  onTabChange(value: string): void {
    this.activeTab = value as DigitalUpsellEditorTab;
  }

  onTimerPreviewState(state: TimerPreviewState): void {
    this.previewState.setDigitalUpsellPreviewTimerState(state);
  }

  onDescriptionChange(html: string): void {
    this.formGroup.controls.description.setValue(html);
  }

  onTooltipContentChange(html: string): void {
    this.formGroup.controls.tooltip.controls.content.setValue(html);
  }

  onProductImageChanged(file: File | null): void {
    this.imageValidationError = null;
    if (!file) {
      this.formGroup.controls.productImageBase64.setValue('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => this.formGroup.controls.productImageBase64.setValue(String(reader.result ?? ''));
    reader.onerror = () => (this.imageValidationError = 'Could not read file. Please try again.');
    reader.readAsDataURL(file);
  }

  onProductImageValidationFailed(reason: 'type' | 'size'): void {
    this.imageValidationError =
      reason === 'type' ? 'Unsupported file type. Use PNG, SVG, or JPG.' : 'File too large. Max 100KB.';
  }

  private updatePercentValidity(): void {
    const percent = this.formGroup.controls.percent.controls;
    this.rangeError = this.isPercent && percent.maxPrice.value <= percent.minPrice.value;
    this.rangeMessage = this.rangeError ? 'Maximum price must be above Minimum price.' : '';
    percent.maxPrice.setErrors(this.rangeError ? { percentRange: true } : null);
  }

  get displayConditionsGroup(): UntypedFormGroup | null {
    return readDisplayConditionsGroup(this.formGroup);
  }
}
